import React, { useEffect, useState } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import sensorIcon from "../../assets/Images/sensor32.png";
import backIcon from "../../assets/Images/Back_16.png";
import * as actions from "../../core/actions/actions";
import IState from "../../core/models/IState";
import RoutesConstants from "../../shared/components/Routes/routes.constants";
import { formatMessage } from "../../translations/connectedIntlProvider";
import "./IAQRealTimeView.css";
import moment from "moment";
import {
  autoRefreshRate,
  chartTheme_Color,
  convertLocalToUTC,
  emptyValueHyphen,
  formatUtcToLocal,
  getKPIThresholdRange,
  getYAxisInterval,
} from "../../constants/common.constant";
import { ILegendDetailsVM } from "../../core/models/IIAQDevicesState";
import { toast } from "react-toastify";
import { insertBreakPoints } from "../../helpers/utility.helper";
import LineChart from "../../shared/components/LineChart/LineChart";
import config from "../../helpers/config.helper";
import ChartSkeleton from "../IAQSensorLineChart/components/ChartSkeleton";
import { tooltipWrapper, tooltipDivider, tooltipRow, tooltipThresholdRow, tooltipSeparator } from "./components/chartToolTip";
import { entries } from "lodash";

const appConfig = config();
const dateTimeFormat = appConfig.REACT_APP_DATE_TIME_FORMAT;
const dateFormat = appConfig.REACT_APP_DATE_FORMAT;

const IAQRealTimeView = (props: any) => {
  let t: NodeJS.Timeout;
  /* eslint-disable jsx-a11y/anchor-is-valid */

  const rtvData = useSelector(
    (state: IState) => state.realtimeViewData.realtimeViewData.data,
  );
  const rtvChartData = useSelector(
    (state: IState) =>
      state.realtimeViewChartData.realtimeViewChartDataState.data,
  );
  const { iaqLegendDetailsVM }: any = useSelector(
    (state: IState) => state.iaqDevices,
  );
  const { realtimeViewData }: any = useSelector(
    (state: IState) => state.realtimeViewData,
  );
  const { realtimeViewChartDataState }: any = useSelector(
    (state: IState) => state.realtimeViewChartData,
  );

  const thresholdData = useSelector(
    (state: IState) => state.realtimeViewData.IIAQSensorThresholdState.data
  );

  const KPI_THRESHOLD_KEY_MAP: Record<string, string> = {
    co2: "CO2_T2",
    pmi_2_5: "PM_T2",
    tvoc: "VOC_T2",
    ch2o: "CH2O_T2",
    co: "CO_T2",
  };

  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date();
  endOfDay.setHours(23, 59, 59, 999);

  const iccid = props.location.state?.iccid ?? "";
  const model = props.location.state?.model ?? "";
  const customerId = props.location.state?.customerId ?? "";
  const room = props.location.state?.room ?? "";
  const floor = props.location.state?.floor ?? "";
  const building = props.location.state?.building ?? "";
  const fromParentPath =
    props.location.state?.fromParentPath ?? RoutesConstants.Sensor;

  const [kpi, setKpi] = useState<string>("co2");
  const [kpiData, setKpiData] = useState<any>(null);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);

  const [dualChartOptions, setDualChartOptions] = useState<any>(null);
  const [kpiChartOptions, setKpiChartOptions] = useState<any>(null);

  const [chartKey, setChartKey] = useState<number>(0);
  const [chartDataLoaded, setChartDataLoaded] = useState<boolean>(false);

  const { language, isLanguageChanged } = useSelector(
    (state: IState) => state.preferences,
  );

  const dispatch = useDispatch();

  const getSensorRange = (name: string) => {
    const sensor = iaqLegendDetailsVM?.data?.find(
      (item: ILegendDetailsVM) => item.KPI.toLowerCase() === name.toLowerCase(),
    );
    return sensor
      ? { MinRange: sensor.MinValue, MaxRange: sensor.MaxValue }
      : { MinRange: 0, MaxRange: 100 };
  };

  const getSensorColor = (name: string, value: number): string | null =>
    getKPIThresholdRange(name, value, thresholdData);

  const getAirIndexColor = (value: number): string | null => {
    if (value <= 50) return "Green";
    if (value <= 100) return "Yellow";
    return "Red";
  };

  const buildKpiChartOptions = (element: any): any => {
    if (!element?.Data?.length) return null;

    const dataPoints = insertBreakPoints(element.Data, 6).map((d: any) => ({
      x: d.x ?? new Date(d.XAxisValue),
      y: d.y ?? d.YAxisValue,
    }));
    const thresholdKey = KPI_THRESHOLD_KEY_MAP[element.KPIName];
    const thresholdValue = thresholdKey && thresholdData ? thresholdData[thresholdKey] : null;

    const thresholdPoints = thresholdValue != null
      ? [{ x: startOfDay, y: thresholdValue }, { x: endOfDay, y: thresholdValue }]
      : null;

    const sensorRange = getSensorRange(element.KPIName);
    let minY = sensorRange.MinRange;
    let maxY = sensorRange.MaxRange;

    return {
      height: 300,
      backgroundColor: chartTheme_Color.CHART_BG,
      theme: "dark2",
      exportEnabled: true,
      zoomEnabled: true,
      animationEnabled: false,
      interactivityEnabled: true,

      title: {
        text: formatMessage(element.KPIName),
        fontColor: chartTheme_Color.TITLE_COLOR,
        fontSize: 15,
        fontWeight: "bold",
        fontFamily: "Segoe UI, Roboto, sans-serif",
        padding: { top: 10, bottom: 6 },
      },

      axisX: {
        minimum: startOfDay,
        maximum: endOfDay,
        // interval: 2,
        // intervalType: "hour",
        labelAutoFit: true,
        labelWrap: false,
        valueFormatString: "HH:mm",
        labelFontColor: chartTheme_Color.AXIS_LABEL_COLOR,
        labelFontSize: 11,
        labelFontFamily: "Segoe UI, sans-serif",
        labelFontWeight: "bold",
        lineColor: chartTheme_Color.AXIS_LINE_COLOR,
        tickColor: chartTheme_Color.AXIS_LINE_COLOR,
        lineThickness: 1,
        tickLength: 5,
        gridColor: chartTheme_Color.GRID_COLOR,
        gridThickness: 1,
        gridDashType: "solid",
        labelAngle: -45,
        title: formatMessage("time"),
        titleFontColor: chartTheme_Color.AXIS_LABEL_COLOR,
        titleFontSize: 12,
        titleFontFamily: "Segoe UI, sans-serif",
        crosshair: {
          enabled: true,
          snapToDataPoint: true,
          lineDashType: "dash",
          color: "#2cd6e8",
          thickness: 2,
          opacity: 1,
          labelFormatter: () => "",
        },
      },

      axisY: {
        title: `${formatMessage(element.KPIName)} (${element.KPIUnit})`,
        minimum: minY,
        maximum: maxY,
        titleFontColor: chartTheme_Color.AXIS_LABEL_COLOR,
        titleFontSize: 12,
        titleFontFamily: "Segoe UI, sans-serif",
        titleFontWeight: "bold",
        labelFontColor: chartTheme_Color.AXIS_LABEL_COLOR,
        labelFontSize: 11,
        labelFontFamily: "Segoe UI, sans-serif",
        lineColor: chartTheme_Color.AXIS_LINE_COLOR,
        tickColor: chartTheme_Color.AXIS_LINE_COLOR,
        lineThickness: 1,
        tickLength: 5,
        gridColor: chartTheme_Color.GRID_COLOR,
        gridThickness: 1,
        gridDashType: "solid",
        interval: getYAxisInterval(minY, maxY),
      },

      toolTip: {
        shared: true,
        backgroundColor: chartTheme_Color.TOOLTIP_BG,
        fontColor: "#ffffff",
        borderColor: chartTheme_Color.CHART_LINE_COLOR,
        borderThickness: 1,
        cornerRadius: 6,
        fontFamily: "Segoe UI, sans-serif",
        fontSize: 13,
        fontWeight: "bold",
        contentFormatter: (e: any) => {
          const date = moment(e.entries[0].dataPoint.x).format("YYYY-MM-DD HH:mm:ss");

          const dataEntry = e.entries.find((entry: any) => entry.dataSeries._index === 0);

          if (!dataEntry) {
            const thresholdOnly = thresholdValue != null
              ? tooltipThresholdRow(
                "#2cd6e8",
                `${formatMessage(element.KPIName)} ${formatMessage("Threshold")}`,
                thresholdValue
              )
              : "";
            return tooltipWrapper(date, thresholdOnly);
          }

          const rows = e.entries.map((entry: any) =>
            tooltipRow(entry.dataSeries.color, entry.dataSeries.name, entry.dataPoint.y)
          ).join('');

          const threshold = thresholdValue != null
            ? tooltipDivider(
              tooltipThresholdRow('#2cd6e8', `${formatMessage(element.KPIName)} ${formatMessage('Threshold')}`, thresholdValue)
            )
            : '';

          return tooltipWrapper(date, rows + threshold);
          // return tooltipWrapper(date, rows);
        }
      },

      data: [
        {
          type: "spline",
          color: chartTheme_Color.CHART_LINE_COLOR,
          fillOpacity: 0.08,
          lineThickness: 2,
          markerType: "circle",
          markerColor: chartTheme_Color.CHART_MARKER_COLOR,
          markerBorderColor: chartTheme_Color.CHART_BG,
          markerBorderThickness: 0,
          markerSize: 3,
          highlightEnabled: true,
          name: formatMessage(element.KPIName),
          xValueFormatString: dateTimeFormat,
          dataPoints,
        },
        {
          type: "line",
          color: "#2cd6e8",
          lineThickness: 2,
          lineDashType: "line",
          markerType: "none",
          name: `${formatMessage(element.KPIName)} ${formatMessage("Threshold")}`,
          showInLegend: false,
          dataPoints: thresholdPoints ?? [],
        }
      ],

      culture: language === "en" ? "en" : "jp",
    };
  };

  const buildDualChartOptions = (data1: any, data2: any): any => {
    if (!data1?.Data?.length || !data2?.Data?.length) return null;

    const makePoints = (raw: any[]) =>
      insertBreakPoints(raw, 6).map((d: any) => ({
        x: d.x ?? new Date(d.XAxisValue),
        y: d.y ?? d.YAxisValue,
      }));

    const dataPoints1 = makePoints(data1.Data);
    const dataPoints2 = makePoints(data2.Data);

    const tmpThreshold = thresholdData?.TMP_H2 ?? null;
    const humThreshold = thresholdData?.HUM_H2 ?? null;

    const thresholdPoints1 = [
      { x: startOfDay, y: tmpThreshold },
      { x: endOfDay, y: tmpThreshold },
    ];
    const thresholdPoints2 = [
      { x: startOfDay, y: humThreshold },
      { x: endOfDay, y: humThreshold },
    ];

    const range1 = getSensorRange(data1.KPIName);
    const range2 = getSensorRange(data2.KPIName);

    const axisBase = {
      labelFontSize: 11,
      labelFontFamily: "Segoe UI, sans-serif",
      lineColor: chartTheme_Color.AXIS_LINE_COLOR,
      tickColor: chartTheme_Color.AXIS_LINE_COLOR,
      lineThickness: 1,
      tickLength: 5,
      gridColor: chartTheme_Color.GRID_COLOR,
      gridThickness: 1,
      gridDashType: "solid",
    };

    return {
      height: 300,
      backgroundColor: chartTheme_Color.CHART_BG,
      theme: "dark2",
      exportEnabled: true,
      zoomEnabled: true,
      animationEnabled: false,
      interactivityEnabled: true,

      title: {
        text: `${formatMessage("temperature")}  /  ${formatMessage("humidity")}`,
        fontColor: chartTheme_Color.TITLE_COLOR,
        fontSize: 15,
        fontWeight: "bold",
        fontFamily: "Segoe UI, Roboto, sans-serif",
        padding: { top: 10, bottom: 6 },
      },

      axisX: {
        ...axisBase,
        minimum: startOfDay,
        maximum: endOfDay,
        // interval: 2,
        // intervalType: "hour",
        labelAutoFit: true,
        labelWrap: false,
        valueFormatString: "HH:mm",
        labelFontColor: chartTheme_Color.AXIS_LABEL_COLOR,
        labelFontWeight: "bold",
        labelAngle: -45,
        title: formatMessage("time"),
        titleFontColor: chartTheme_Color.AXIS_LABEL_COLOR,
        titleFontSize: 12,
        titleFontFamily: "Segoe UI, sans-serif",
        crosshair: {
          enabled: true,
          snapToDataPoint: true,
          lineDashType: "dash",
          color: "#2cd6e8",
          thickness: 2,
          opacity: 1,
          labelFormatter: () => "",
        },
      },

      axisY: {
        ...axisBase,
        title: `${formatMessage(data1.KPIName)} (${data1.KPIUnit})`,
        minimum: range1.MinRange,
        maximum: range1.MaxRange,
        titleFontColor: chartTheme_Color.CHART_LINE_COLOR,
        titleFontSize: 12,
        titleFontFamily: "Segoe UI, sans-serif",
        titleFontWeight: "bold",
        labelFontColor: chartTheme_Color.CHART_LINE_COLOR,
      },

      axisY2: {
        ...axisBase,
        title: `${formatMessage(data2.KPIName)} (${data2.KPIUnit})`,
        minimum: range2.MinRange,
        maximum: range2.MaxRange,
        titleFontColor: chartTheme_Color.CHART_LINE_COLOR2,
        titleFontSize: 12,
        titleFontFamily: "Segoe UI, sans-serif",
        titleFontWeight: "bold",
        labelFontColor: chartTheme_Color.CHART_LINE_COLOR2,
        gridThickness: 0,
      },

      toolTip: {
        shared: true,
        backgroundColor: "#111827",
        fontColor: "#ffffff",
        borderColor: "rgba(255,255,255,0.15)",
        borderThickness: 1,
        cornerRadius: 8,
        fontFamily: "Segoe UI, sans-serif",
        fontSize: 13,
        contentFormatter: (e: any) => {
          const date = moment(e.entries[0].dataPoint.x).format("YYYY-MM-DD HH:mm");

          const tempEntry = e.entries.find((entry: any) => entry.dataSeries._index === 0);
          const humEntry = e.entries.find((entry: any) => entry.dataSeries._index === 1);

          if (!tempEntry && !humEntry) {
            const thresholdOnly =
              (tmpThreshold != null
                ? tooltipThresholdRow("#2cd6e8", formatMessage("Temp_Threshold"), tmpThreshold)
                : "") +
              (humThreshold != null
                ? tooltipThresholdRow("#FFD54F", formatMessage("Humidity_Threshold"), humThreshold)
                : "");
            return tooltipWrapper(date, thresholdOnly);
          }

          const tempBlock = tempEntry
            ? tooltipRow(
              tempEntry.dataSeries.color,
              tempEntry.dataSeries.name,
              tempEntry.dataPoint.y
            ) +
            (tmpThreshold != null
              ? tooltipThresholdRow("#2cd6e8", formatMessage("Temp_Threshold"), tmpThreshold)
              : "")
            : "";

          const humBlock = humEntry
            ? tooltipRow(
              humEntry.dataSeries.color,
              humEntry.dataSeries.name,
              humEntry.dataPoint.y
            ) +
            (humThreshold != null
              ? tooltipThresholdRow("#FFD54F", formatMessage("Humidity_Threshold"), humThreshold)
              : "")
            : "";

          const body = (tempBlock && humBlock)
            ? tempBlock + tooltipSeparator() + humBlock
            : tempBlock || humBlock;

          return tooltipWrapper(date, body);
        }
      },
      data: [
        {
          type: "spline",
          axisYType: "primary",
          color: chartTheme_Color.CHART_LINE_COLOR,
          fillOpacity: 0.08,
          lineThickness: 2,
          markerType: "circle",
          markerColor: chartTheme_Color.CHART_MARKER_COLOR,
          markerBorderColor: chartTheme_Color.CHART_BG,
          markerBorderThickness: 0,
          markerSize: 3,
          highlightEnabled: true,
          name: formatMessage(data1.KPIName),
          xValueFormatString: dateTimeFormat,
          dataPoints: dataPoints1,
        },
        {
          type: "spline",
          axisYType: "secondary",
          color: chartTheme_Color.CHART_LINE_COLOR2,
          fillOpacity: 0.08,
          lineThickness: 2,
          markerType: "circle",
          markerColor: chartTheme_Color.CHART_MARKER_COLOR2,
          markerBorderColor: chartTheme_Color.CHART_BG,
          markerBorderThickness: 0,
          markerSize: 3,
          highlightEnabled: true,
          name: formatMessage(data2.KPIName),
          xValueFormatString: dateTimeFormat,
          dataPoints: dataPoints2,
        },
        {
          type: "line",
          axisYType: "primary",
          color: "#2cd6e8",
          lineThickness: 2,
          markerType: "none",
          name: formatMessage("Temp_Threshold"),
          showInLegend: false,
          dataPoints: thresholdPoints1 ?? [],
        },
        {
          type: "line",
          axisYType: "secondary",
          color: "#FFD54F",
          lineThickness: 2,
          markerType: "none",
          name: formatMessage("Humidity_Threshold"),
          showInLegend: false,
          dataPoints: thresholdPoints2 ?? [],
        },
      ],

      culture: language === "en" ? "en" : "jp",
    };
  };

  const getAllDataService = () => {
    setAutoRefresh(false);
    if (iccid) {
      dispatch(actions.getRealtimeViewData({ ICCID: iccid }));
      dispatch(
        actions.getRealtimeViewChartData({
          ICCID: iccid,
          Date: convertLocalToUTC(moment(new Date()).format(dateFormat)), //converted Local date time to UTC
        }),
      );
      dispatch(actions.getThresholdData({ ICCID: iccid }));
    } else {
      props.history.push(RoutesConstants.Sensor)
    }
    // dispatch(actions.getIAQLegendDetails());
  };

  useEffect(() => {
    dispatch(actions.resetUserSettingState());
    getAllDataService();
    dispatch(actions.resetGetSensorByIdState());
    return () => {
      // dispatch(actions.resetIAQLegendDetailsState());
      dispatch(actions.resetRealtimeViewDataState());
      dispatch(actions.resetRealtimeViewChartDataState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLanguageChanged) {
      getAllDataService();
      dispatch(actions.resetIsLanguageChanged());
    }
  }, [isLanguageChanged]);

  useEffect(() => {
    if (autoRefresh) {
      t = setInterval(() => {
        getAllDataService();
      }, autoRefreshRate);
    } else {
      clearInterval(t);
    }
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  useEffect(() => {
    if (!autoRefresh) setAutoRefresh(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtvData]);

  useEffect(() => {
    if (realtimeViewChartDataState.error) {
      toast.error(formatMessage(realtimeViewChartDataState.error));
      dispatch(actions.resetRealtimeViewChartDataState());
    }
  }, [realtimeViewChartDataState.error]);

  useEffect(() => {
    if (realtimeViewData.error) {
      toast.error(formatMessage(realtimeViewData.error));
      dispatch(actions.resetRealtimeViewDataState());
    }
  }, [realtimeViewData.error]);

  useEffect(() => {
    if (!rtvChartData?.ChartData?.length) return;

    const found = rtvChartData.ChartData.find(
      (item: any) => item.KPIName === kpi,
    );
    const resolvedKpiData = found ?? rtvChartData.ChartData[0];
    setKpiData(resolvedKpiData);
    if (rtvChartData.ChartData[0] && rtvChartData.ChartData[1]) {
      setDualChartOptions(
        buildDualChartOptions(
          rtvChartData.ChartData[0],
          rtvChartData.ChartData[1],
        ),
      );
    }
    if (resolvedKpiData) {
      setKpiChartOptions(buildKpiChartOptions(resolvedKpiData));
    }

    setChartDataLoaded(true);
    setChartKey((prev) => prev + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtvChartData, thresholdData]);

  useEffect(() => {
    if (!rtvChartData?.ChartData?.length) return;

    const found = rtvChartData.ChartData.find(
      (item: any) => item.KPIName === kpi,
    );
    if (!found) return;

    setKpiData(found);
    setKpiChartOptions(buildKpiChartOptions(found));
    setChartKey((prev) => prev + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpi, thresholdData]);

  const handleChange = (event: any) => {
    setKpi(event.target.value);
  };

  const refreshSensorDataClickHandler = () => {
    setDualChartOptions(null);
    setKpiChartOptions(null);
    setChartDataLoaded(false);
    getAllDataService();
  };

  const backToSensorDataHandler = () => {
    dispatch(actions.resetRealtimeViewDataState());
    dispatch(actions.resetRealtimeViewChartDataState());
    props.history.push({
      pathname: fromParentPath,
      state: {
        iccid,
        model,
        building: props.location.state.building,
        location: props.location.state.location,
        floor: props.location.state.floor,
        customerId: props.location.state.customerId,
        room: props.location.state.room,
      },
    });
  };

  const gotoChartClickHandler = () => {
    dispatch(actions.resetUserSettingState());
    props.history.push({
      pathname: RoutesConstants.IAQSensorDataChart,
      state: {
        iccid,
        model,
        building: props.location.state.building,
        location: props.location.state.location,
        floor: props.location.state.floor,
        customerId: props.location.state.customerId,
        room: props.location.state.room,
        latestPayloadTime: props.location.state.latestPayloadTime,
        fromPath: RoutesConstants.IAQRealTimeView,
        fromParentPath,
      },
    });
  };

  const gotoIAQTableViewClickHandler = () => {
    dispatch(actions.resetUserSettingState());
    props.history.push({
      pathname: RoutesConstants.IAQSensorData,
      state: {
        iccid,
        model,
        building: props.location.state.building,
        location: props.location.state.location,
        floor: props.location.state.floor,
        customerId: props.location.state.customerId,
        room: props.location.state.room,
        latestPayloadTime: props.location.state.latestPayloadTime,
        fromPath: RoutesConstants.IAQRealTimeView,
        fromParentPath,
      },
    });
  };
  return (
    <React.Fragment>
      <div className="icon-nav navbar-fixed-top device-nav-bar">
        <div className="container-fluid">
          <div className="filter-left-area">
            <ul>
              <li>
                <h1>
                  <img src={sensorIcon} alt="" />{" "}
                  <FormattedMessage id="Real_Time_View_Title" /> {" - "}{" "}
                  <FormattedMessage id="Sensor_Label_ICCID" />: {" " + iccid}
                </h1>
              </li>
            </ul>
          </div>

          <div className="add-list float-right">
            <ul>
              <li title={formatMessage("Back")}>
                <a data-toggle="modal" onClick={backToSensorDataHandler}>
                  <img src={backIcon} alt="back" />
                </a>
              </li>
              <li title={formatMessage("table_view")}>
                <a data-toggle="modal" onClick={gotoIAQTableViewClickHandler}>
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-table"
                    viewBox="0 0 16 16"
                  >
                    <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm15 2h-4v3h4zm0 4h-4v3h4zm0 4h-4v3h3a1 1 0 0 0 1-1zm-5 3v-3H6v3zm-5 0v-3H1v2a1 1 0 0 0 1 1zm-4-4h4V8H1zm0-4h4V4H1zm5-3v3h4V4zm4 4H6v3h4z" />
                  </svg>
                </a>
              </li>
              <li title={formatMessage("Chart")}>
                <a data-toggle="modal" onClick={gotoChartClickHandler}>
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-graph-up"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fillRule="evenodd"
                      d="M0 0h1v15h15v1H0V0zm10 3.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-1 0V4.9l-3.613 4.417a.5.5 0 0 1-.74.037L7.06 6.767l-3.656 5.027a.5.5 0 0 1-.808-.588l4-5.5a.5.5 0 0 1 .758-.06l2.609 2.61L13.445 4H10.5a.5.5 0 0 1-.5-.5z"
                    />
                  </svg>
                </a>
              </li>
              <li title={formatMessage("Refresh")}>
                <a data-toggle="modal" onClick={refreshSensorDataClickHandler}>
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-arrow-repeat"
                    viewBox="0 0 16 16"
                  >
                    <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z" />
                    <path
                      fillRule="evenodd"
                      d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z"
                    />
                  </svg>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      {props.location.state && (
        <div className="col-12 mb-1 data-heading" id="data-heading">
          <div className="d-md-flex">
            <div className="mr-md-3 w-100">
              <h4>
                <FormattedMessage id="Sensor_Table_Col_CustomerId" />
              </h4>
              <h5>{customerId || emptyValueHyphen}</h5>
            </div>
            <div className="mx-md-3 w-100">
              <h4>
                <FormattedMessage id="Sensor_Table_Col_Building" />
              </h4>
              <h5>{building || emptyValueHyphen}</h5>
            </div>
            <div className="mx-md-3 w-100">
              <h4>
                <FormattedMessage id="Sensor_Table_Col_Floor" />
              </h4>
              <h5>{floor || emptyValueHyphen}</h5>
            </div>
            <div className="mx-md-3 w-100">
              <h4>
                <FormattedMessage id="Sensor_Table_Col_Room" />
              </h4>
              <h5>{room || emptyValueHyphen}</h5>
            </div>
            <div className="ml-md-3 w-100">
              <h4>
                <FormattedMessage id="Latest_payload_time" />
              </h4>
              <h5>{formatUtcToLocal(rtvData?.CreatedAt) || emptyValueHyphen}</h5>
            </div>
          </div>
        </div>
      )}

      <div className="p-4">
        <div
          className="row g-0 justify-content-center"
          style={{ height: "calc(100vh - 110px)" }}
        >
          <div className="card card-body">
            <div className="container mt-4">
              <div className="row g-3">
                <div className="col-md-3 col-6 mb-4">
                  <div className="metric-card">
                    <div className="metric-title">
                      <FormattedMessage id="temperature" />
                    </div>
                    <div className="metric-value">
                      <span>{rtvData.Temprature}</span> °C
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6 mb-4">
                  <div className="metric-card">
                    <div className="metric-title">
                      <FormattedMessage id="co2" />
                    </div>
                    <div className="metric-value">
                      <span
                        className={`ft${getSensorColor("co2", rtvData.CO2)}`}
                      >
                        {rtvData.CO2}
                      </span>{" "}
                      ppm
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6 mb-4">
                  <div className="metric-card">
                    <div className="metric-title">
                      <FormattedMessage id="pmi_2_5" />
                    </div>
                    <div className="metric-value">
                      <span
                        className={`ft${getSensorColor("pmi_2_5", rtvData.PMI25)}`}
                      >
                        {rtvData.PMI25}
                      </span>{" "}
                      µg/m³
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6 mb-4">
                  <div className="metric-card">
                    <div className="metric-title">
                      <FormattedMessage id="tvoc" />
                    </div>
                    <div className="metric-value">
                      <span
                        className={`ft${getSensorColor("tvoc", rtvData.TVOC)}`}
                      >
                        {rtvData.TVOC}
                      </span>{" "}
                      ppb
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6 mb-4">
                  <div className="metric-card">
                    <div className="metric-title">
                      <FormattedMessage id="humidity" />
                    </div>
                    <div className="metric-value">
                      <span>{rtvData.Humidity}</span> %Rh
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6 mb-4">
                  <div className="metric-card">
                    <div className="metric-title">
                      <FormattedMessage id="ch2o" />
                    </div>
                    <div className="metric-value">
                      <span
                        className={`ft${getSensorColor("ch2o", rtvData.CH2O)}`}
                      >
                        {rtvData.CH2O}
                      </span>{" "}
                      ppb
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6 mb-4">
                  <div className="metric-card">
                    <div className="metric-title">
                      <FormattedMessage id="co" />
                    </div>
                    <div className="metric-value">
                      <span className={`ft${getSensorColor("co", rtvData.CO)}`}>
                        {rtvData.CO}
                      </span>{" "}
                      ppm
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6 mb-4">
                  <div
                    className={`metric-card bg${getAirIndexColor(rtvData.AirIndex)}`}
                  >
                    <div className="metric-title">
                      <FormattedMessage id="aqi" />
                    </div>
                    <div className="metric-value">
                      {rtvData.AirIndex && Math.round(rtvData.AirIndex)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="container mt-2">
              <div className="row">
                <div className="col-12 text-left mb-2">
                  <div className="d-flex justify-content-end">
                    <h3 className="mx-2 my-2">KPI : </h3>
                    <select
                      value={kpi}
                      className="form-control w-25"
                      onChange={handleChange}
                    >
                      <option value="co2">{formatMessage("co2")}</option>
                      <option value="pmi_2_5">
                        {formatMessage("pmi_2_5")}
                      </option>
                      <option value="tvoc">{formatMessage("tvoc")}</option>
                      <option value="ch2o">{formatMessage("ch2o")}</option>
                      <option value="co">{formatMessage("co")}</option>
                    </select>
                  </div>
                </div>


                <div className="col-12 col-lg-6 mt-0 mb-4">
                  <div className="kpi-card-real-time">
                    <div
                      className="kpi-chart-container mt-2 iaq-canvasjs-theme"
                      style={{ height: "300px" }}
                    >
                      {dualChartOptions && chartDataLoaded ? (
                        <LineChart key={`dual-${chartKey}`} index={0} options={dualChartOptions} />
                      ) : chartDataLoaded ? (
                        <div style={{ color: "#aaa", textAlign: "center", paddingTop: "120px" }}>
                          <FormattedMessage id="NO_DATA_AVAILABLE" />
                        </div>
                      ) : (
                        <ChartSkeleton height={300} />
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-12 col-lg-6 mt-0 mb-4">
                  <div className="kpi-card-real-time">
                    <div
                      className="kpi-chart-container mt-2 iaq-canvasjs-theme"
                      style={{ height: "300px" }}
                    >
                      {kpiChartOptions && chartDataLoaded ? (
                        <LineChart key={`kpi-${chartKey}-${kpi}`} index={1} options={kpiChartOptions} />
                      ) : chartDataLoaded ? (
                        <div style={{ color: "#aaa", textAlign: "center", paddingTop: "120px" }}>
                          <FormattedMessage id="NO_DATA_AVAILABLE" />
                        </div>
                      ) : (
                        <ChartSkeleton height={300} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default injectIntl(IAQRealTimeView);
